import type { StcRecord } from '../types/stc'

/**
 * Extract claim/consultation date from STC record.
 * STC uses formData.date; MICS uses formData.micsClaimDate; fallback to createdAt.
 */
function getClaimDateValue(record: StcRecord): string {
  const raw = String(
    record.formType === 'MICS'
      ? record.formData?.micsClaimDate ?? record.formData?.date ?? record.createdAt ?? ''
      : record.formData?.date ?? record.createdAt ?? ''
  ).trim()
  return raw
}

/**
 * Parse date string to { year, month }.
 * Supports: YYYY-MM-DD, DD/MM/YYYY, ISO strings.
 */
function parseClaimMonthYear(value: string): { year: number; month: number } | null {
  const raw = String(value || '').trim()
  if (!raw) return null

  // ISO: YYYY-MM-DD
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) {
    const year = Number.parseInt(iso[1], 10)
    const month = Number.parseInt(iso[2], 10)
    if (Number.isFinite(year) && Number.isFinite(month) && month >= 1 && month <= 12) {
      return { year, month }
    }
  }

  // DD/MM/YYYY
  const dmy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (dmy) {
    const year = Number.parseInt(dmy[3], 10)
    const month = Number.parseInt(dmy[2], 10)
    if (Number.isFinite(year) && Number.isFinite(month) && month >= 1 && month <= 12) {
      return { year, month }
    }
  }

  // Fallback: Date.parse
  const parsed = new Date(raw)
  if (Number.isFinite(parsed.getTime())) {
    return {
      year: parsed.getFullYear(),
      month: parsed.getMonth() + 1,
    }
  }

  return null
}

/**
 * PCRS pays on ~15th of month M for claims in month M-1.
 * Eligible for refresh = consultation month < current month.
 * (Current-month claims won't be paid until the 15th of next month.)
 *
 * Missing date: treat as eligible (conservative).
 */
export function isEligibleForPcrsRefresh(record: StcRecord): boolean {
  const dateValue = getClaimDateValue(record)
  const claimMonthYear = parseClaimMonthYear(dateValue)

  if (!claimMonthYear) return true // conservative: allow refresh if date unparseable

  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1

  // Eligible if claim month is before current month (or claim year is before current year)
  if (claimMonthYear.year < currentYear) return true
  if (claimMonthYear.year > currentYear) return false
  return claimMonthYear.month < currentMonth
}
